import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  Badge,
  Box,
  Button,
  Callout,
  Card,
  Checkbox,
  Dialog,
  Flex,
  Grid,
  SegmentedControl,
  Select,
  Separator,
  Switch,
  Text,
  TextArea,
  TextField,
  Tooltip,
} from "@radix-ui/themes";
import {
  CalendarIcon,
  ChatBubbleIcon,
  EnvelopeClosedIcon,
  InfoCircledIcon,
  MobileIcon,
  PersonIcon,
  PlusIcon,
  TimerIcon,
  TrashIcon,
} from "@radix-ui/react-icons";
import { ToastAlert } from "components";
import { useLocationContext, useProfileContext } from "context";
import {
  useCreateAutomation,
  useDeleteAutomation,
  useGetWhatsappAccountPhoneNumbers,
  useGetWhatsappCredentialAccounts,
  useGetAllBrands,
  useMarketingAutomations,
  useMarketingWhatsappTemplates,
  useUpdateAutomation,
} from "hooks";
import { useContactTags } from "hooks/bMarketing/useContacts";
import { allTimezones, useTimezoneSelect } from "react-timezone-select";
import { useToast } from "../../context/ToastContext";
import ContactFilterBuilder, { needsValue } from "./ContactFilterBuilder";
import MarketingWhatsappPreview from "./MarketingWhatsappPreview";
import WhatsappMessagingLimitHelp from "./WhatsappMessagingLimitHelp";

const SECTION_ORDER = ["review", "promotion"];
const TRIGGER_OPTIONS = {
  guest_visit: {
    label: "Review request",
    subType: "review",
    description: "Send after a guest visits through the guest login flow.",
  },
  birthday: {
    label: "Birthday offer",
    subType: "promotion",
    description: "Send before or on the contact's birthday at the selected local time.",
  },
};
const BIRTHDAY_SEND_TIMING_OPTIONS = [
  { value: 0, label: "On their birthday" },
  { value: 1, label: "1 day before" },
  { value: 3, label: "3 days before" },
  { value: 7, label: "7 days before" },
  { value: 14, label: "14 days before" },
  { value: 21, label: "21 days before" },
  { value: 28, label: "28 days before" },
  { value: 30, label: "30 days before birthday" },
];
const DEFAULT_WHATSAPP_AUTOMATION = {
  name: "",
  description: "",
  type: "whatsapp",
  sub_type: "review",
  trigger_type: "guest_visit",
  associated_template_id: "",
  birthday_send_time: "09:00",
  birthday_send_offset_days: 14,
  birthday_timezone: "Europe/London",
  whatsapp_phone_number_id: "",
  audience_level: "organisation",
  audience_brands: [],
  audience_locations: [],
  is_active: true,
  send_delay_minutes: 15,
  send_delay_unit: "minutes",
  repeat_enabled: false,
  repeat_cooldown_value: 1,
  repeat_cooldown_unit: "months",
  eligibility_filters: { match: "all", rules: [] },
};

const formatAutomationDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const AutomationTimestamp = ({ value }) => {
  const formattedValue = formatAutomationDateTime(value);

  return (
    <Tooltip content={formattedValue}>
      <Text
        as="div"
        size="1"
        weight="medium"
        style={{ width: "100%", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {formattedValue}
      </Text>
    </Tooltip>
  );
};

const AutomationTriggerBadge = ({ triggerType }) => {
  const isBirthday = triggerType === "birthday";
  const TriggerIcon = isBirthday ? CalendarIcon : PersonIcon;

  return (
    <Badge color={isBirthday ? "violet" : "blue"} variant="soft" style={{ alignSelf: "flex-start" }}>
      <TriggerIcon width="12" height="12" />
      {isBirthday ? "Birthday trigger" : "Post-visit trigger"}
    </Badge>
  );
};
const DELAY_OPTIONS = [
  { value: "0-minutes", label: "Instant", send_delay_minutes: 0, send_delay_unit: "minutes" },
  { value: "15-minutes", label: "15 minutes", send_delay_minutes: 15, send_delay_unit: "minutes" },
  { value: "30-minutes", label: "30 minutes", send_delay_minutes: 30, send_delay_unit: "minutes" },
  { value: "1-hours", label: "1 hour", send_delay_minutes: 1, send_delay_unit: "hours" },
  { value: "2-hours", label: "2 hours", send_delay_minutes: 2, send_delay_unit: "hours" },
];
const REPEAT_OPTIONS = [
  { value: "1-months", label: "1 month", repeat_cooldown_value: 1, repeat_cooldown_unit: "months" },
  { value: "2-months", label: "2 months", repeat_cooldown_value: 2, repeat_cooldown_unit: "months" },
  { value: "3-months", label: "3 months", repeat_cooldown_value: 3, repeat_cooldown_unit: "months" },
];
const EMPTY_TEMPLATE_OPTION_VALUE = "__no_whatsapp_templates_available__";
const WHATSAPP_AUTOMATION_INTEGRATION_REQUIRED_MESSAGE = "Connect a WhatsApp integration before creating WhatsApp automations.";

function buildEligibilityPayload(filters) {
  const rules = (filters?.rules || []).filter((rule) => {
    if (!rule.field || !rule.operator) return false;
    if (needsValue(rule.operator)) {
      if (Array.isArray(rule.value)) return rule.value.length > 0;
      return rule.value !== "" && rule.value !== undefined && rule.value !== null;
    }
    return true;
  });

  return rules.length ? { match: filters?.match || "all", rules } : {};
}

function getTemplateSelectValue(template) {
  const templateId = template?.id ?? template?.template_id;
  if (templateId === undefined || templateId === null) return null;

  const value = String(templateId).trim();
  return value || null;
}

function getSelectedTemplateValue(templateId) {
  if (templateId === undefined || templateId === null) return "";

  return String(templateId);
}

function formatAudienceLevel(level) {
  return level.charAt(0).toUpperCase() + level.slice(1);
}

function formatSubType(subType) {
  if (subType === "review") {
    return "Review request automations";
  }

  if (subType === "promotion") {
    return "Birthday offer automations";
  }

  return subType;
}

function formatBirthdaySendTiming(offsetDays) {
  const numericOffset = Number(offsetDays || 0);
  if (numericOffset === 0) return "Day of birthday";
  return BIRTHDAY_SEND_TIMING_OPTIONS.find((option) => option.value === numericOffset)?.label
    || `${numericOffset} days before birthday`;
}

function getWhatsappPhoneNumberLabel(phoneNumber) {
  const displayNumber = phoneNumber?.display_phone_number || phoneNumber?.phone_number || "";
  return displayNumber || "Unknown phone number";
}

function formatMessagingLimit(account) {
  const usage = account?.messaging_limit_usage || {};
  const tier = account?.messaging_limit_tier || usage.tier || "Not refreshed";
  const limit = account?.messaging_limit ?? usage.limit;
  const used = Number(usage.used || 0);
  const remaining = usage.remaining;

  if (limit === null || typeof limit === "undefined") {
    return {
      tier,
      detail: usage.limit_known ? "Meta reports an unlimited messaging tier." : "Messaging limit is unknown. Sending stays paused until the limit is confirmed.",
      color: "gray",
    };
  }

  return {
    tier,
    detail: `${Number(remaining || 0).toLocaleString()} of ${Number(limit || 0).toLocaleString()} unique recipients remaining in the rolling 24-hour window (${used.toLocaleString()} used).`,
    color: Number(remaining || 0) > 0 ? "green" : "red",
  };
}

function formatDelayLabel(minutes, unit) {
  const normalizedMinutes = Number(minutes || 0);

  if (normalizedMinutes === 0) {
    return "Instant";
  }

  if (unit === "hours") {
    return `${normalizedMinutes} hour${normalizedMinutes === 1 ? "" : "s"}`;
  }

  if (unit === "days") {
    return `${normalizedMinutes} day${normalizedMinutes === 1 ? "" : "s"}`;
  }

  return `${normalizedMinutes} minute${normalizedMinutes === 1 ? "" : "s"}`;
}

function buildDefaultAutomation() {
  return {
    ...DEFAULT_WHATSAPP_AUTOMATION,
    audience_brands: [],
    audience_locations: [],
    eligibility_filters: { match: "all", rules: [] },
  };
}

export function buildDraftAutomation(automation = null) {
  return {
    ...buildDefaultAutomation(),
    ...(automation || {}),
    type: "whatsapp",
    trigger_type: automation?.trigger_type || "guest_visit",
    sub_type: automation?.sub_type || TRIGGER_OPTIONS[automation?.trigger_type || "guest_visit"]?.subType || "review",
    birthday_send_time: automation?.birthday_send_time || "09:00",
    birthday_send_offset_days: automation?.birthday_send_offset_days ?? DEFAULT_WHATSAPP_AUTOMATION.birthday_send_offset_days,
    birthday_timezone: automation?.birthday_timezone || "",
    whatsapp_phone_number_id: automation?.whatsapp_phone_number_id || "",
    audience_brands: automation?.audience_brands || [],
    audience_locations: automation?.audience_locations || [],
    eligibility_filters: {
      match: automation?.eligibility_filters?.match || "all",
      rules: automation?.eligibility_filters?.rules
        ? automation.eligibility_filters.rules.map((rule) => ({ ...rule }))
        : [],
    },
  };
}

export function buildAutomationPayload(automation, organisationId) {
  const audienceLevel = automation.audience_level || "organisation";
  const triggerType = automation.trigger_type || "guest_visit";
  const subType = TRIGGER_OPTIONS[triggerType]?.subType || "review";

  return {
    organisation: organisationId,
    name: automation.name.trim(),
    description: automation.description.trim(),
    type: "whatsapp",
    sub_type: subType,
    associated_template_id: automation.associated_template_id,
    trigger_type: triggerType,
    birthday_send_time: triggerType === "birthday" ? automation.birthday_send_time : null,
    birthday_send_offset_days: triggerType === "birthday" ? Number(automation.birthday_send_offset_days ?? 14) : 0,
    birthday_timezone: triggerType === "birthday" ? automation.birthday_timezone : null,
    whatsapp_phone_number_id: automation.whatsapp_phone_number_id,
    audience_level: audienceLevel,
    audience_brands: audienceLevel === "brand" ? automation.audience_brands : [],
    audience_locations: audienceLevel === "location" ? automation.audience_locations : [],
    is_active: automation.is_active,
    send_delay_minutes: triggerType === "guest_visit" ? Number(automation.send_delay_minutes || 0) : 0,
    send_delay_unit: triggerType === "guest_visit" ? automation.send_delay_unit || "minutes" : "minutes",
    repeat_enabled: triggerType === "guest_visit" ? automation.repeat_enabled : false,
    repeat_cooldown_value: triggerType === "guest_visit" && automation.repeat_enabled ? Number(automation.repeat_cooldown_value || 1) : null,
    repeat_cooldown_unit: triggerType === "guest_visit" && automation.repeat_enabled ? automation.repeat_cooldown_unit || "months" : null,
    eligibility_filters: buildEligibilityPayload(automation.eligibility_filters),
  };
}

function getDelayOptionValue(automation) {
  const matchingOption = DELAY_OPTIONS.find((option) => (
    option.send_delay_minutes === Number(automation.send_delay_minutes || 0)
    && option.send_delay_unit === (automation.send_delay_unit || "minutes")
  ));

  return matchingOption?.value || "15-minutes";
}

function getRepeatOptionValue(automation) {
  const matchingOption = REPEAT_OPTIONS.find((option) => (
    option.repeat_cooldown_value === Number(automation.repeat_cooldown_value || 1)
    && option.repeat_cooldown_unit === (automation.repeat_cooldown_unit || "months")
  ));

  return matchingOption?.value || "1-months";
}

function ChannelFilter({ selectedSegment, onSelectedSegmentChange }) {
  return (
    <SegmentedControl.Root value={selectedSegment} onValueChange={onSelectedSegmentChange}>
      <SegmentedControl.Item value="all">All</SegmentedControl.Item>
      <SegmentedControl.Item value="email">
        <Flex gap="1" align="center">
          <EnvelopeClosedIcon />
          <Text>Email</Text>
        </Flex>
      </SegmentedControl.Item>
      <SegmentedControl.Item value="sms">
        <Flex gap="1" align="center">
          <MobileIcon />
          <Text>SMS</Text>
        </Flex>
      </SegmentedControl.Item>
      <SegmentedControl.Item value="whatsapp">
        <Flex gap="1" align="center">
          <ChatBubbleIcon />
          <Text>WhatsApp</Text>
        </Flex>
      </SegmentedControl.Item>
    </SegmentedControl.Root>
  );
}

const dialogBodyStyle = {
  flex: "1 1 auto",
  minHeight: 0,
  height: "min(620px, calc(85vh - 170px))",
  overflow: "hidden",
  alignItems: "stretch",
};

const dialogFormPaneStyle = {
  flex: "1 1 0",
  minWidth: 360,
  minHeight: 0,
  overflowY: "auto",
  paddingRight: "var(--space-2)",
};

const dialogSeparatorStyle = {
  alignSelf: "stretch",
  minHeight: "100%",
};

function TemplatePreviewPanel({ template }) {
  const previewTemplate = template ? { ...template, buttons: template.buttons || [] } : null;

  return (
    <Flex direction="column" gap="2" style={{ flex: "0 0 420px", minWidth: 320, minHeight: 0, overflow: "hidden" }}>
      <Text weight="medium" size="3" align="center" justify="center">Template preview</Text>
      <Box
        style={{
          flex: "1 1 0",
          minHeight: 0,
          border: "1px solid var(--gray-a6)",
          borderRadius: "var(--radius-3)",
          background: "var(--gray-2)",
          overflow: "auto",
        }}
      >
        {previewTemplate ? (
          <MarketingWhatsappPreview template={previewTemplate} />
        ) : (
          <Flex direction="column" align="center" justify="center" gap="2" height="100%" p="5" style={{ textAlign: "center" }}>
            <Text weight="medium" size="2">No template selected</Text>
            <Text size="2" color="gray">Choose a WhatsApp template to preview it here.</Text>
          </Flex>
        )}
      </Box>
    </Flex>
  );
}

export function AutomationFields({ automation, setAutomation, templates, brands, locations, titleSuffix, organisationId, contactTags }) {
  const updateAutomation = useCallback((patch) => {
    setAutomation((current) => ({ ...current, ...patch }));
  }, [setAutomation]);

  const selectableBrands = (brands || []).filter((brand) => !brand.is_default);
  const selectableTemplates = (templates || []).filter((template) => getTemplateSelectValue(template));
  const selectedTemplate = selectableTemplates.find((template) => (
    getTemplateSelectValue(template) === getSelectedTemplateValue(automation.associated_template_id)
  ));
  const selectedTemplateAccountId = selectedTemplate?.whatsappConfig;
  const { data: whatsappAccountsData } = useGetWhatsappCredentialAccounts(organisationId);
  const whatsappAccounts = whatsappAccountsData?.data?.accounts || [];
  const selectedWhatsappAccount = whatsappAccounts.find((account) => String(account.id) === String(selectedTemplateAccountId)) || null;
  const messagingLimit = useMemo(() => formatMessagingLimit(selectedWhatsappAccount), [selectedWhatsappAccount]);
  const { data: phoneNumbersData, isLoading: isLoadingPhoneNumbers } = useGetWhatsappAccountPhoneNumbers(
    organisationId,
    selectedTemplateAccountId,
    Boolean(selectedTemplateAccountId),
  );
  const whatsappPhoneNumbers = phoneNumbersData?.data?.phone_numbers || [];
  const selectedTrigger = automation.trigger_type || "guest_visit";
  const timezones = { ...allTimezones, "Europe/Berlin": "Frankfurt" };
  const { options: timezoneOptions } = useTimezoneSelect({ labelStyle: "original", timezones });
  const selectedBirthdayOffset = Number(automation.birthday_send_offset_days ?? 0);
  const hasPresetBirthdayOffset = BIRTHDAY_SEND_TIMING_OPTIONS.some(
    (option) => option.value === selectedBirthdayOffset,
  );
  const hasTimezoneOption = timezoneOptions?.some(
    (option) => option.value === automation.birthday_timezone,
  );

  useEffect(() => {
    if (selectedTrigger !== "birthday" || automation.birthday_timezone) return;
    let relevant = locations || [];
    if (automation.audience_level === "location") {
      const ids = new Set((automation.audience_locations || []).map(String));
      relevant = relevant.filter((location) => ids.has(String(location.id)));
    } else if (automation.audience_level === "brand") {
      const ids = new Set((automation.audience_brands || []).map(String));
      relevant = relevant.filter((location) => ids.has(String(location.brand?.id ?? location.brand_id ?? location.brand)));
    }
    const timezones = [...new Set(relevant.map((location) => location.timezone).filter(Boolean))];
    if (timezones.length === 1) updateAutomation({ birthday_timezone: timezones[0] });
  }, [
    selectedTrigger,
    automation.birthday_timezone,
    automation.audience_level,
    automation.audience_brands,
    automation.audience_locations,
    locations,
    updateAutomation,
  ]);

  return (
    <Flex gap="4" px="1" style={dialogBodyStyle}>
      <Flex direction="column" gap="4" style={dialogFormPaneStyle}>
        <Flex direction="column" gap="1" style={{ alignSelf: "stretch" }}>
          <Text weight="medium" size="2">Name</Text>
          <TextField.Root
            value={automation.name}
            onChange={(event) => updateAutomation({ name: event.target.value })}
            placeholder={`Enter ${titleSuffix} name`}
          />
        </Flex>

        <Flex direction="column" gap="1" style={{ alignSelf: "stretch" }}>
          <Text weight="medium" size="2">Description (recommended)</Text>
          <TextArea
            value={automation.description}
            onChange={(event) => updateAutomation({ description: event.target.value })}
            placeholder="This description will help you identify the automation's purpose."
            style={{ height: 80 }}
          />
        </Flex>

        <Flex gap="4">
          <Flex direction="column" gap="1" style={{ flex: 1, minWidth: 0 }}>
            <Text weight="medium" size="2">Type</Text>
            <Select.Root value="whatsapp" disabled>
              <Select.Trigger style={{ width: "100%", marginTop: "4px" }} />
              <Select.Content>
                <Select.Item value="whatsapp">
                  <Flex gap="2" align="center">
                    <ChatBubbleIcon />
                    <Text>WhatsApp</Text>
                  </Flex>
                </Select.Item>
              </Select.Content>
            </Select.Root>
          </Flex>

          <Flex direction="column" gap="1" style={{ flex: 1, minWidth: 0 }}>
            <Text weight="medium" size="2">Trigger</Text>
            <Select.Root
              value={selectedTrigger}
              onValueChange={(value) => updateAutomation({
                trigger_type: value,
                sub_type: TRIGGER_OPTIONS[value]?.subType || "review",
                ...(value === "birthday" && selectedTrigger !== "birthday" ? {
                  birthday_send_offset_days: 14,
                  birthday_timezone: automation.birthday_timezone || "Europe/London",
                } : {}),
              })}
            >
              <Select.Trigger style={{ width: "100%", marginTop: "4px" }} />
              <Select.Content>
                {Object.entries(TRIGGER_OPTIONS).map(([value, option]) => (
                  <Select.Item key={value} value={value}>{option.label}</Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Flex>
        </Flex>

        <Text size="1" color="gray">{TRIGGER_OPTIONS[selectedTrigger]?.description}</Text>

        <Flex direction="column" gap="1" style={{ alignSelf: "stretch" }}>
          <Text weight="medium" size="2">Associated template</Text>
          <Select.Root
            value={getSelectedTemplateValue(automation.associated_template_id)}
            onValueChange={(value) => updateAutomation({
              associated_template_id: value,
              whatsapp_phone_number_id: "",
            })}
          >
            <Select.Trigger style={{ width: "100%", marginTop: "4px" }} placeholder="Select an approved template" />
            <Select.Content>
              {selectableTemplates.length > 0 ? (
                selectableTemplates.map((template) => {
                  const templateValue = getTemplateSelectValue(template);
                  return <Select.Item key={templateValue} value={templateValue}>{template.name}</Select.Item>;
                })
              ) : (
                <Select.Item value={EMPTY_TEMPLATE_OPTION_VALUE} disabled>No approved WhatsApp templates available</Select.Item>
              )}
            </Select.Content>
          </Select.Root>
        </Flex>

        <Flex direction="column" gap="1" style={{ alignSelf: "stretch" }}>
          <Text weight="medium" size="2">Sender phone number</Text>
          <Select.Root
            value={automation.whatsapp_phone_number_id || ""}
            onValueChange={(value) => updateAutomation({ whatsapp_phone_number_id: value })}
            disabled={!selectedTemplateAccountId || isLoadingPhoneNumbers || whatsappPhoneNumbers.length === 0}
          >
            <Select.Trigger
              style={{ width: "100%", marginTop: "4px" }}
              placeholder={isLoadingPhoneNumbers ? "Loading phone numbers..." : selectedTemplateAccountId ? "Select sender phone number" : "Select a template first"}
            />
            <Select.Content>
              {isLoadingPhoneNumbers ? (
                <Select.Item value="__loading_whatsapp_phone_numbers__" disabled>Loading phone numbers...</Select.Item>
              ) : whatsappPhoneNumbers.length > 0 ? (
                whatsappPhoneNumbers.map((phoneNumber) => (
                  <Select.Item key={phoneNumber.id} value={String(phoneNumber.id)}>
                    {getWhatsappPhoneNumberLabel(phoneNumber)}
                  </Select.Item>
                ))
              ) : (
                <Select.Item value="__no_whatsapp_phone_numbers__" disabled>No phone numbers available</Select.Item>
              )}
            </Select.Content>
          </Select.Root>
          {!isLoadingPhoneNumbers && selectedTemplateAccountId && whatsappPhoneNumbers.length === 0 ? (
            <Text size="1" color="red">No sender phone numbers were found for this WhatsApp account.</Text>
          ) : null}
        </Flex>

        {selectedTemplate ? (
          <Callout.Root color={messagingLimit.color} variant="surface">
            <Callout.Icon>
              <InfoCircledIcon />
            </Callout.Icon>
            <Callout.Text>
              <Flex direction="column" gap="1">
                <Text size="2">
                  WhatsApp messaging tier: {messagingLimit.tier}. {messagingLimit.detail} Messages wait at the limit and resume automatically when capacity is available.
                </Text>
                <WhatsappMessagingLimitHelp />
              </Flex>
            </Callout.Text>
          </Callout.Root>
        ) : null}

        {selectedTrigger === "guest_visit" ? (
          <>
            <Flex direction="column" gap="1" style={{ alignSelf: "stretch" }}>
              <Text weight="medium" size="2">Send delay</Text>
              <Select.Root
                value={getDelayOptionValue(automation)}
                onValueChange={(value) => {
                  const selectedOption = DELAY_OPTIONS.find((option) => option.value === value) || DELAY_OPTIONS[1];
                  updateAutomation({
                    send_delay_minutes: selectedOption.send_delay_minutes,
                    send_delay_unit: selectedOption.send_delay_unit,
                  });
                }}
              >
                <Select.Trigger style={{ width: "100%", marginTop: "4px" }} />
                <Select.Content>
                  {DELAY_OPTIONS.map((option) => (
                    <Select.Item key={option.value} value={option.value}>{option.label}</Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Flex>

            <Flex direction="column" gap="1" style={{ alignSelf: "stretch" }}>
              <Flex align="center" justify="between">
                <Text weight="medium" size="2">Repeat automation</Text>
                <Switch
                  checked={automation.repeat_enabled}
                  onCheckedChange={(checked) => updateAutomation({ repeat_enabled: checked })}
                />
              </Flex>
              {automation.repeat_enabled ? (
                <Flex direction="column" gap="1" style={{ alignSelf: "stretch" }}>
                  <Text size="1" color="gray">Cooldown period before sending again</Text>
                  <Select.Root
                    value={getRepeatOptionValue(automation)}
                    onValueChange={(value) => {
                      const selectedOption = REPEAT_OPTIONS.find((option) => option.value === value) || REPEAT_OPTIONS[0];
                      updateAutomation({
                        repeat_cooldown_value: selectedOption.repeat_cooldown_value,
                        repeat_cooldown_unit: selectedOption.repeat_cooldown_unit,
                      });
                    }}
                  >
                    <Select.Trigger style={{ width: "100%" }} />
                    <Select.Content>
                      {REPEAT_OPTIONS.map((option) => (
                        <Select.Item key={option.value} value={option.value}>{option.label}</Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                </Flex>
              ) : null}
            </Flex>
          </>
        ) : (
          <>
          <Flex gap="4" wrap="wrap" style={{ alignSelf: "stretch" }}>
            <Flex direction="column" gap="1" style={{ flex: "1 1 0", minWidth: 0 }}>
              <Text weight="medium" size="2">Days before birthday</Text>
              <Select.Root
                value={String(selectedBirthdayOffset)}
                onValueChange={(value) => updateAutomation({ birthday_send_offset_days: Number(value) })}
              >
                <Select.Trigger style={{ width: "100%" }} />
                <Select.Content position="popper">
                  {!hasPresetBirthdayOffset ? (
                    <Select.Item value={String(selectedBirthdayOffset)}>{selectedBirthdayOffset} days before</Select.Item>
                  ) : null}
                  {BIRTHDAY_SEND_TIMING_OPTIONS.map((option) => (
                    <Select.Item key={option.value} value={String(option.value)}>{option.label}</Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Flex>

            <Flex direction="column" gap="1" style={{ flex: "0 0 190px", minWidth: 160 }}>
              <Text weight="medium" size="2">Local send time</Text>
              <TextField.Root
                type="time"
                value={automation.birthday_send_time || ""}
                onChange={(event) => updateAutomation({ birthday_send_time: event.target.value })}
              />
            </Flex>
            <Flex direction="column" gap="1" style={{ flex: "1 1 220px", minWidth: 200 }}>
              <Text weight="medium" size="2">Timezone</Text>
              <Select.Root
                value={automation.birthday_timezone || ""}
                onValueChange={(value) => updateAutomation({ birthday_timezone: value })}
              >
                <Select.Trigger placeholder="Select a timezone" style={{ width: "100%" }} />
                <Select.Content position="popper">
                  <Select.Group>
                    {automation.birthday_timezone && !hasTimezoneOption ? (
                      <Select.Item value={automation.birthday_timezone}>{automation.birthday_timezone}</Select.Item>
                    ) : null}
                    {timezoneOptions?.map((timezone) => (
                      <Select.Item key={timezone.value} value={timezone.value}>{timezone.label}</Select.Item>
                    ))}
                  </Select.Group>
                </Select.Content>
              </Select.Root>
            </Flex>
          </Flex>
          <Text size="1" color="gray">Past occurrences are not sent on activation. Feb 29 birthdays send on Feb 28 in non-leap years.</Text>
          {!automation.birthday_timezone ? (
            <Text size="1" color="red">Timezone selection is required before this Birthday Automation can be saved or activated.</Text>
          ) : null}
          </>
        )}

        <Flex align="center" justify="between">
          <Text weight="medium" size="2">Active</Text>
          <Switch
            checked={automation.is_active}
            onCheckedChange={(checked) => updateAutomation({ is_active: checked })}
          />
        </Flex>

        <Flex direction="column" gap="1" style={{ alignSelf: "stretch" }}>
          <Text weight="medium" size="2">Audience level</Text>
          <Select.Root
            value={automation.audience_level}
            onValueChange={(value) => updateAutomation({
              audience_level: value,
              audience_brands: value === "brand" ? automation.audience_brands : [],
              audience_locations: value === "location" ? automation.audience_locations : [],
            })}
          >
            <Select.Trigger style={{ width: "100%" }} />
            <Select.Content>
              <Select.Item value="organisation">Organisation (All locations)</Select.Item>
              <Select.Item value="brand">Brand (Select brands)</Select.Item>
              <Select.Item value="location">Location (Select locations)</Select.Item>
            </Select.Content>
          </Select.Root>
        </Flex>

        {automation.audience_level === "brand" ? (
          <Flex direction="column" gap="1" style={{ alignSelf: "stretch" }}>
            <Text weight="medium" size="2">Select Brands</Text>
            <Flex direction="column" gap="2" mt="2" style={{ maxHeight: 150, overflowY: "auto" }}>
              {selectableBrands.map((brand) => (
                <Flex key={brand.id} align="center" gap="2">
                  <Checkbox
                    checked={automation.audience_brands.includes(brand.id)}
                    onCheckedChange={(checked) => {
                      updateAutomation({
                        audience_brands: checked
                          ? [...automation.audience_brands, brand.id]
                          : automation.audience_brands.filter((currentId) => currentId !== brand.id),
                      });
                    }}
                  />
                  <Text size="2">{brand.name}</Text>
                </Flex>
              ))}
              {selectableBrands.length === 0 ? <Text size="2" color="gray">No brands available</Text> : null}
            </Flex>
          </Flex>
        ) : null}

        {automation.audience_level === "location" ? (
          <Flex direction="column" gap="1" style={{ alignSelf: "stretch" }}>
            <Text weight="medium" size="2">Select Locations</Text>
            <Grid columns="3" gap="3" mt="2" style={{ maxHeight: 200, overflowY: "auto" }}>
              {locations.map((location) => (
                <Flex key={location.id} align="center" gap="2">
                  <Checkbox
                    checked={automation.audience_locations.includes(location.id)}
                    onCheckedChange={(checked) => {
                      updateAutomation({
                        audience_locations: checked
                          ? [...automation.audience_locations, location.id]
                          : automation.audience_locations.filter((currentId) => currentId !== location.id),
                      });
                    }}
                  />
                  <Text size="2" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {location.name}
                  </Text>
                </Flex>
              ))}
            </Grid>
            {locations.length === 0 ? <Text size="2" color="gray" mt="2">No locations available</Text> : null}
          </Flex>
        ) : null}

        <Flex direction="column" gap="2" style={{ alignSelf: "stretch" }}>
          <Flex direction="column" gap="1">
            <Text weight="medium" size="2">Eligibility filters (optional)</Text>
            <Text size="1" color="gray">
              Limit this automation to contacts matching attribute rules (e.g. age group, tags).
              Leave empty to include every contact in the selected audience.
            </Text>
          </Flex>
          <ContactFilterBuilder
            matchMode={automation.eligibility_filters?.match || "all"}
            onMatchModeChange={(value) => updateAutomation({
              eligibility_filters: { ...automation.eligibility_filters, match: value },
            })}
            rules={automation.eligibility_filters?.rules || []}
            onRulesChange={(rules) => updateAutomation({
              eligibility_filters: { ...automation.eligibility_filters, rules },
            })}
            locations={locations}
            tags={contactTags || []}
          />
        </Flex>
      </Flex>
      <Separator orientation="vertical" size="4" style={dialogSeparatorStyle} />
      <TemplatePreviewPanel template={selectedTemplate} />
    </Flex>
  );
}

export default function MarketingWhatsappAutomations({
  selectedSegment,
  onSelectedSegmentChange,
}) {
  const navigate = useNavigate();
  const { userProfile } = useProfileContext();
  const { locationsData } = useLocationContext();
  const { setToastMessage } = useToast();
  const { data: brandsData } = useGetAllBrands(userProfile?.organisation_id);
  const { contactTags } = useContactTags(userProfile?.organisation_id);
  const { whatsappTemplates, isLoading: isLoadingTemplates } = useMarketingWhatsappTemplates(userProfile?.organisation_id);
  const { data: whatsappAccountsData } = useGetWhatsappCredentialAccounts(userProfile?.organisation_id);
  const { data: automations = [], isLoading: isLoadingAutomations } = useMarketingAutomations(userProfile?.organisation_id, "whatsapp");
  const { mutate: createAutomation, isPending: isCreating } = useCreateAutomation();
  const { mutate: updateAutomation, isPending: isUpdating } = useUpdateAutomation();
  const { mutate: deleteAutomation, isPending: isDeleting } = useDeleteAutomation();

  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [editedAutomation, setEditedAutomation] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [newAutomation, setNewAutomation] = useState(buildDefaultAutomation);

  const approvedTemplates = useMemo(() => {
    return whatsappTemplates.filter((template) => template.reviewStatus === "approved");
  }, [whatsappTemplates]);
  const whatsappAccounts = useMemo(() => whatsappAccountsData?.data?.accounts || [], [whatsappAccountsData]);
  const hasWhatsappIntegration = whatsappAccounts.length > 0;
  const hasApprovedTemplates = approvedTemplates.length > 0;
  const isLoading = isLoadingTemplates || isLoadingAutomations;
  const createAutomationDisabledReason = !hasWhatsappIntegration
    ? WHATSAPP_AUTOMATION_INTEGRATION_REQUIRED_MESSAGE
    : !hasApprovedTemplates
      ? "Create an approved WhatsApp template to enable WhatsApp automations"
      : "";

  useEffect(() => {
    if (selectedAutomation) {
      setEditedAutomation(buildDraftAutomation(selectedAutomation));
      return;
    }

    setEditedAutomation(null);
  }, [selectedAutomation]);

  useEffect(() => {
    if (!isCreateDialogOpen) {
      setNewAutomation(buildDefaultAutomation());
    }
  }, [isCreateDialogOpen]);

  const hasChanges = useMemo(() => {
    if (!selectedAutomation || !editedAutomation) {
      return false;
    }

    return (
      selectedAutomation.name !== editedAutomation.name ||
      selectedAutomation.description !== editedAutomation.description ||
      selectedAutomation.trigger_type !== editedAutomation.trigger_type ||
      selectedAutomation.birthday_send_time !== editedAutomation.birthday_send_time ||
      selectedAutomation.birthday_send_offset_days !== editedAutomation.birthday_send_offset_days ||
      selectedAutomation.birthday_timezone !== editedAutomation.birthday_timezone ||
      selectedAutomation.associated_template_id !== editedAutomation.associated_template_id ||
      selectedAutomation.whatsapp_phone_number_id !== editedAutomation.whatsapp_phone_number_id ||
      selectedAutomation.audience_level !== editedAutomation.audience_level ||
      selectedAutomation.send_delay_minutes !== editedAutomation.send_delay_minutes ||
      selectedAutomation.send_delay_unit !== editedAutomation.send_delay_unit ||
      selectedAutomation.repeat_enabled !== editedAutomation.repeat_enabled ||
      selectedAutomation.repeat_cooldown_value !== editedAutomation.repeat_cooldown_value ||
      selectedAutomation.repeat_cooldown_unit !== editedAutomation.repeat_cooldown_unit ||
      selectedAutomation.is_active !== editedAutomation.is_active ||
      JSON.stringify(selectedAutomation.audience_brands || []) !== JSON.stringify(editedAutomation.audience_brands || []) ||
      JSON.stringify(selectedAutomation.audience_locations || []) !== JSON.stringify(editedAutomation.audience_locations || []) ||
      JSON.stringify(buildEligibilityPayload(selectedAutomation.eligibility_filters)) !== JSON.stringify(buildEligibilityPayload(editedAutomation.eligibility_filters))
    );
  }, [selectedAutomation, editedAutomation]);

  const groupedAutomations = useMemo(() => {
    const groups = {};

    SECTION_ORDER.forEach((subType) => {
      const matchingAutomations = automations
        .filter((automation) => automation.sub_type === subType)
        .sort((left, right) => new Date(left.created_at) - new Date(right.created_at));

      if (matchingAutomations.length > 0) {
        groups[subType] = matchingAutomations;
      }
    });

    return groups;
  }, [automations]);

  const visibleSections = useMemo(() => {
    return SECTION_ORDER.filter((subType) => Array.isArray(groupedAutomations[subType]) && groupedAutomations[subType].length > 0);
  }, [groupedAutomations]);

  const openCreateDialog = () => {
    if (!hasWhatsappIntegration) {
      setToastMessage(WHATSAPP_AUTOMATION_INTEGRATION_REQUIRED_MESSAGE);
      return;
    }

    if (!hasApprovedTemplates) {
      setToastMessage("Create an approved WhatsApp template before building WhatsApp automations");
      return;
    }

    setIsCreateDialogOpen(true);
  };

  const handleCreateAutomation = () => {
    if (!newAutomation.name.trim()) {
      setToastMessage("Add an automation name before creating it");
      return;
    }

    if (!newAutomation.associated_template_id) {
      setToastMessage("Select an approved WhatsApp template before creating the automation");
      return;
    }

    if (!newAutomation.whatsapp_phone_number_id) {
      setToastMessage("Select a WhatsApp sender phone number before creating the automation");
      return;
    }

    if (newAutomation.trigger_type === "birthday" && (!newAutomation.birthday_send_time || !newAutomation.birthday_timezone)) {
      setToastMessage("Choose the local birthday send time and timezone before creating the automation");
      return;
    }

    createAutomation(buildAutomationPayload(newAutomation, userProfile.organisation_id), {
      onSuccess: () => {
        setIsCreateDialogOpen(false);
        setToastMessage("WhatsApp automation created");
      },
      onError: () => {
        setToastMessage("Unable to create the WhatsApp automation right now");
      },
    });
  };

  const handleUpdateAutomation = () => {
    if (!selectedAutomation || !editedAutomation) {
      return;
    }

    if (!editedAutomation.name.trim()) {
      setToastMessage("Add an automation name before saving");
      return;
    }

    if (!editedAutomation.associated_template_id) {
      setToastMessage("Select an approved WhatsApp template before saving");
      return;
    }

    if (!editedAutomation.whatsapp_phone_number_id) {
      setToastMessage("Select a WhatsApp sender phone number before saving");
      return;
    }

    if (editedAutomation.trigger_type === "birthday" && (!editedAutomation.birthday_send_time || !editedAutomation.birthday_timezone)) {
      setToastMessage("Choose the local birthday send time and timezone before saving");
      return;
    }

    updateAutomation({
      automationId: selectedAutomation.automation_id,
      data: buildAutomationPayload(editedAutomation, userProfile.organisation_id),
    }, {
      onSuccess: () => {
        setSelectedAutomation(null);
        setToastMessage("WhatsApp automation updated");
      },
      onError: () => {
        setToastMessage("Unable to update the WhatsApp automation right now");
      },
    });
  };

  const handleDeleteAutomation = () => {
    if (!selectedAutomation) {
      return;
    }

    deleteAutomation(selectedAutomation.automation_id, {
      onSuccess: () => {
        setIsDeleteDialogOpen(false);
        setSelectedAutomation(null);
        setToastMessage("WhatsApp automation removed");
      },
      onError: () => {
        setToastMessage("Unable to delete the WhatsApp automation right now");
      },
    });
  };

  const hasAutomations = visibleSections.length > 0;

  return (
    <>
      <Flex direction="column" gap="5" style={{ alignSelf: "stretch", alignItems: "center", flex: "1 0 0" }}>
        <Flex justify="between" align="center" style={{ alignSelf: "stretch" }}>
          <ChannelFilter selectedSegment={selectedSegment} onSelectedSegmentChange={onSelectedSegmentChange} />

          {createAutomationDisabledReason ? (
            <Tooltip content={createAutomationDisabledReason}>
              <span style={{ display: "inline-flex" }}>
                <Button variant="soft" onClick={openCreateDialog} disabled>
                  <PlusIcon width="16" height="16" />
                  Create automation
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button variant="soft" onClick={openCreateDialog} disabled={isCreating}>
              <PlusIcon width="16" height="16" />
              Create automation
            </Button>
          )}
        </Flex>

        <Flex direction="column" gap="5" style={{ alignSelf: "stretch", width: "100%", overflow: "auto", height: "100%", flex: "1 0 0" }}>
          {isLoading && automations.length === 0 ? (
            <Flex direction="column" style={{ justifyContent: "center", alignItems: "center", alignSelf: "stretch", border: "1px dashed var(--gray-a6)", borderRadius: "var(--radius-4)", height: "100%", padding: "var(--space-6)" }}>
              <Text color="gray">Loading WhatsApp automations...</Text>
            </Flex>
          ) : !hasAutomations ? (
            <Flex direction="column" gap="3" style={{ justifyContent: "center", alignItems: "center", alignSelf: "stretch", border: "1px dashed var(--gray-a6)", borderRadius: "var(--radius-4)", height: "100%", padding: "var(--space-6)" }}>
              {!hasWhatsappIntegration ? (
                <>
                  <Text color="gray">Connect a WhatsApp integration before building an automation.</Text>
                </>
              ) : !hasApprovedTemplates ? (
                <>
                  <Text color="gray">Create an approved WhatsApp template before building an automation.</Text>
                  <Button variant="soft" onClick={() => navigate("/marketing/templates", { state: { selectedSegment: "whatsapp" } })}>
                    Open WhatsApp templates
                  </Button>
                </>
              ) : (
                <Text color="gray">No automations found. Create your first WhatsApp automation to get started.</Text>
              )}
            </Flex>
          ) : (
            visibleSections.map((subType, index) => {
              const automationsInSection = groupedAutomations[subType];
              if (!automationsInSection?.length) {
                return null;
              }

              return (
                <React.Fragment key={subType}>
                  {index > 0 ? <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} /> : null}
                  <Flex direction="column" gap="4" style={{ alignSelf: "stretch" }}>
                    <Text size="3" weight="medium">{formatSubType(subType)}</Text>
                    <Grid columns="3" gap="4" style={{ alignSelf: "stretch", width: "100%" }}>
                      {automationsInSection.map((automation) => (
                        <Card
                          key={automation.automation_id}
                          style={{ cursor: "pointer", display: "flex", flexDirection: "column" }}
                          onClick={() => setSelectedAutomation(automation)}
                        >
                          <Flex direction="column" gap="5" justify="between" height="100%">
                            <Flex direction="column" gap="3">
                              <Flex justify="between" align="center" style={{ alignSelf: "stretch" }}>
                                <Flex gap="2" align="center">
                                  <TimerIcon />
                                  <Text weight="medium" size="2">{automation.name}</Text>
                                </Flex>
                                <Badge color={automation.is_active ? "green" : "gray"} variant="soft">
                                  {automation.is_active ? "Active" : "Inactive"}
                                </Badge>
                              </Flex>

                              {automation.description ? (
                                <Text
                                  color="gray"
                                  size="1"
                                  style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                  }}
                                >
                                  {automation.description}
                                </Text>
                              ) : null}
                              <AutomationTriggerBadge triggerType={automation.trigger_type} />
                              {automation.whatsapp_waiting_count > 0 ? (
                                <Tooltip content="These messages will resume automatically when WhatsApp capacity is available.">
                                  <Badge color="amber" variant="soft" style={{ alignSelf: "flex-start" }}>
                                    {automation.whatsapp_waiting_count} waiting — WhatsApp limit
                                  </Badge>
                                </Tooltip>
                              ) : null}
                              <Grid columns="2" gap="3" style={{ width: "100%" }}>
                                <Flex direction="column" gap="1" style={{ minWidth: 0 }}>
                                  <Text size="1" color="gray">Created on</Text>
                                  <AutomationTimestamp value={automation.created_at} />
                                </Flex>
                                <Flex direction="column" gap="1" style={{ minWidth: 0 }}>
                                  <Text size="1" color="gray">Updated on</Text>
                                  <AutomationTimestamp value={automation.updated_at} />
                                </Flex>
                              </Grid>
                            </Flex>

                            <Flex gap="2" align="center" justify="between">
                              <Flex gap="2" align="center">
                                <PersonIcon />
                                <Text size="1" color="gray">
                                  {formatAudienceLevel(automation.audience_level)} level
                                  {automation.target_locations_count > 0 ? (
                                    <> · {automation.target_locations_count} location{automation.target_locations_count !== 1 ? "s" : ""}</>
                                  ) : null}
                                </Text>
                              </Flex>
                              <Text size="1" color="gray">
                                {automation.trigger_type === "birthday"
                                  ? `${formatBirthdaySendTiming(automation.birthday_send_offset_days)} · ${automation.birthday_send_time || "No time"} · ${automation.birthday_timezone || "Timezone required"}`
                                  : formatDelayLabel(automation.send_delay_minutes, automation.send_delay_unit)}
                              </Text>
                            </Flex>
                          </Flex>
                        </Card>
                      ))}
                    </Grid>
                  </Flex>
                </React.Fragment>
              );
            })
          )}
        </Flex>
      </Flex>

      <Dialog.Root open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <Dialog.Content style={{ height: "100%", width: "min(1120px, calc(100vw - 32px))", maxWidth: 1120, display: "flex", flexDirection: "column" }}>
          <Dialog.Title>Create Automation</Dialog.Title>
          <Dialog.Description size="2" mb="4" color="gray">
            Set up a new automation for your marketing campaigns.
          </Dialog.Description>

          <AutomationFields
            automation={newAutomation}
            setAutomation={setNewAutomation}
            templates={approvedTemplates}
            brands={brandsData || []}
            locations={locationsData || []}
            titleSuffix="automation"
            organisationId={userProfile?.organisation_id}
            contactTags={contactTags || []}
          />

          <Flex gap="3" mt="5" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">Cancel</Button>
            </Dialog.Close>
            <Button
              onClick={handleCreateAutomation}
              disabled={
                isCreating ||
                !newAutomation.name.trim() ||
                !newAutomation.associated_template_id ||
                !newAutomation.whatsapp_phone_number_id ||
                (newAutomation.trigger_type === "birthday" && (!newAutomation.birthday_send_time || !newAutomation.birthday_timezone))
              }
              loading={isCreating}
            >
              Create automation
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <Dialog.Root open={Boolean(selectedAutomation)} onOpenChange={(open) => !open && setSelectedAutomation(null)}>
        <Dialog.Content style={{ height: "100%", width: "min(1120px, calc(100vw - 32px))", maxWidth: 1120, display: "flex", flexDirection: "column" }}>
          <Dialog.Title>Edit Automation</Dialog.Title>
          <Dialog.Description size="2" mb="4" color="gray">
            Modify the automation settings below.
          </Dialog.Description>

          {editedAutomation ? (
            <AutomationFields
              automation={editedAutomation}
              setAutomation={setEditedAutomation}
              templates={approvedTemplates}
              brands={brandsData || []}
              locations={locationsData || []}
              titleSuffix="automation"
              organisationId={userProfile?.organisation_id}
              contactTags={contactTags || []}
            />
          ) : null}

          <Flex gap="3" mt="5" justify="between">
            <Button variant="soft" color="red" onClick={() => setIsDeleteDialogOpen(true)} disabled={isDeleting}>
              <TrashIcon />
              Delete
            </Button>
            <Flex gap="3">
              <Dialog.Close>
                <Button variant="soft" color="gray">Close</Button>
              </Dialog.Close>
              <Button
                onClick={handleUpdateAutomation}
                disabled={
                  isUpdating ||
                  !hasChanges ||
                  !editedAutomation?.name.trim() ||
                  !editedAutomation?.associated_template_id ||
                  !editedAutomation?.whatsapp_phone_number_id ||
                  (editedAutomation?.trigger_type === "birthday" && (!editedAutomation?.birthday_send_time || !editedAutomation?.birthday_timezone))
                }
                loading={isUpdating}
              >
                Update automation
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <AlertDialog.Root open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialog.Content style={{ maxWidth: 450 }}>
          <AlertDialog.Title>Delete WhatsApp automation?</AlertDialog.Title>
          <AlertDialog.Description size="2">
            This removes the WhatsApp automation from marketing.
          </AlertDialog.Description>

          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">Cancel</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button color="red" onClick={handleDeleteAutomation} disabled={isDeleting}>
                {isDeleting ? "Deleting..." : "Delete"}
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>

      <ToastAlert />
    </>
  );
}
